import React, { useEffect, useMemo } from "react"
import { Box, VStack } from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { useParams } from "react-router"
import get from "lodash/get"
import set from "lodash/set"
import { BadgeElement, BadgeTemplate } from "sharedTypes/badge"
import EditorPane from "components/CanvasEditor/EditorPane"
import ColorProperty from "components/CanvasEditor/Inspector/ColorProperty"
import { useGetDocument, useGetSelection } from "components/CanvasEditor/Editor"
import { MixedValuesProvider } from "components/CanvasEditor/Inspector/MixedValues"
import { useBadgeElement } from "../../contexts/BadgeElementContext"
import { usePrinterSwatches } from "../../hooks/usePrinterColorConstraints"
import { elementToFormValues, formValuesToElementUpdate } from "../../utils/elementTransformers"
import {
  flipGroup, GEOMETRY, groupBox, groupFormValues, mixedFields, placeInBox, rotateGroup,
} from "../../utils/groupValues"
import { ElementUpdate } from "../../hooks/useElementTransforms"
import { BadgeElementFormValues } from "./BadgeSidePanelForm"
import PositionSizeFields from "./Fields/PositionSizeFields"
import BorderControl from "./Fields/BorderControl"
import TransformControls from "./Fields/TransformControls"
import { canFlip, hasBorder, hasFill } from "./Fields/CommonStylingFields"
import ElementSpecificFields from "./ElementSpecificFields"

type Props = {
  elements: BadgeElement[]
}

/**
 * The panel for several selected elements: what they have in common, each change applied to all
 * of them as one step. Position and size are the group's box; a field where they differ shows
 * "Mixed". What each one says (its text, its field, its image) isn't offered.
 */
const BadgeGroupPanel = ({ elements }: Props) => {
  const { eventId } = useParams<{ eventId: string }>()
  const { handleElementsChange } = useBadgeElement()
  const getTemplate = useGetDocument<BadgeTemplate>()
  const getSelection = useGetSelection()
  const swatches = usePrinterSwatches()

  const values = useMemo(() => groupFormValues(elements), [elements])
  const mixed = useMemo(() => mixedFields(elements), [elements])
  const { control, reset, watch } = useForm<BadgeElementFormValues>({ defaultValues: values })

  // The panel follows the elements when they change elsewhere (the canvas, undo)
  const valuesKey = JSON.stringify(values)
  useEffect(() => {
    reset(values)
  }, [valuesKey])

  const selected = () => {
    const { selectedIds } = getSelection()

    return getTemplate().badgeData.elements.filter((element) => selectedIds.includes(element.id))
  }

  const each = (update: (element: BadgeElement) => ElementUpdate) => {
    handleElementsChange(new Map(selected().map((element) => [element.id, update(element)])))
  }

  // What the user changes in a field is set on every element, the rest of each left as it is
  useEffect(() => {
    const subscription = watch((formValues, { name, type }) => {
      if (type !== "change" || !name) return

      const current = selected()
      if (GEOMETRY.includes(name)) {
        const box = groupBox(current)
        const next = { ...box, [name]: Number(get(formValues, name)) }
        handleElementsChange(placeInBox(current, box, next))

        return
      }

      const value = get(formValues, name)
      each((element) => {
        const elementValues = structuredClone(elementToFormValues(element))
        set(elementValues, name, value)

        return formValuesToElementUpdate(elementValues)
      })
    })

    return () => subscription.unsubscribe()
  }, [watch])

  // The selection turns and flips as one picture, as in design tools
  const rotateBy = (degrees: number) => handleElementsChange(rotateGroup(selected(), degrees))
  const flip = (axis: "horizontal" | "vertical") => handleElementsChange(flipGroup(selected(), axis))

  const types = elements.map((element) => element.type)
  const withFill = types.every(hasFill)
  const withBorder = types.every(hasBorder)

  return (
    <MixedValuesProvider value={mixed}>
      <Box py={2}>
        <VStack spacing={6} align="stretch">
          <PositionSizeFields control={control} />
          {(withBorder || withFill) && (
            <EditorPane title="Style">
              {withBorder && <BorderControl control={control} />}
              {withFill && <ColorProperty control={control} name="fill" label="Fill" {...swatches} />}
            </EditorPane>
          )}
          <TransformControls
            control={control}
            hideFlipControls={!types.every(canFlip)}
            onRotateBy={rotateBy}
            onFlip={flip}
          />
          <ElementSpecificFields control={control} eventId={eventId} group />
        </VStack>
      </Box>
    </MixedValuesProvider>
  )
}

export default BadgeGroupPanel
